'use strict';

// 미답변 문항 최종 제출 차단 — 순수 표시/판정 헬퍼 (Vue 의존 없음, tests/ 에서 node --test 로 검증)
//
// 캠페인 옵션 문자열. 백엔드 DFCampaigns.process_options 와 공유하는 계약값이므로 바꾸지 말 것.
// (관리자 화면 EditingCampaignAttr.vue 의 체크박스 value 와 동일)
const BLOCK_UNANSWERED_OPTION = '미답변제출차단';
// PUT /company/submit 이 미답변 차단으로 거부할 때 409 바디의 code (백엔드 계약)
const UNANSWERED_REQUIRED_CODE = 'UNANSWERED_REQUIRED';

// 차단 모달의 sub_type 배지용 ko 번역 키. 매핑에 없는 타입은 null → 배지 숨김 (원문 노출 금지)
const SUB_TYPE_LABEL_KEYS = {
    table: '표',
    radio: '선택(단일)',
    checkbox: '선택(복수)',
    input_text: '주관식',
    textbox: '주관식',
    input_number: '숫자',
    file_URL2: '첨부',
    materiality: '중대성',
    priority: '우선순위'
};

// 차단 모달의 사유 배지용 ko 번역 키. 백엔드 items[].reason 계약값. 매핑에 없으면 null → 배지 숨김
// (reason 은 상위 선택은 했는데 하위 checkbox 를 안 고른 경우에만 내려온다. 표는 missing_cells 가 사유)
const REASON_LABEL_KEYS = {
    child_checkbox: '하위 항목 미선택'
};

// process_options 는 배열이 정상이지만 JSON 문자열로 올 가능성도 막아 둔다.
function hasBlockUnansweredOption(process_options) {
    let _options = process_options;
    if (typeof _options === 'string') {
        try {
            _options = JSON.parse(_options);
        } catch (_error) {
            return false;
        }
    }
    return Array.isArray(_options) && _options.includes(BLOCK_UNANSWERED_OPTION);
}

function subTypeLabelKey(sub_type) {
    return Object.prototype.hasOwnProperty.call(SUB_TYPE_LABEL_KEYS, sub_type)
        ? SUB_TYPE_LABEL_KEYS[sub_type]
        : null;
}

function reasonLabelKey(reason) {
    return Object.prototype.hasOwnProperty.call(REASON_LABEL_KEYS, reason)
        ? REASON_LABEL_KEYS[reason]
        : null;
}

// 사이드바(template_contents 말단 문항) 순서로 정렬한다. 순서에 없는 문항은 백엔드 순서 그대로 뒤에 붙인다.
function sortGroupsByOrder(groups, orderedQuestionCodes) {
    if (!Array.isArray(orderedQuestionCodes) || !orderedQuestionCodes.length) return groups;
    const _index_by_code = new Map();
    orderedQuestionCodes.forEach((_code, _idx) => {
        if (!_index_by_code.has(_code)) _index_by_code.set(_code, _idx);
    });
    const _known = groups.filter(_group => _index_by_code.has(_group.question_code));
    const _unknown = groups.filter(_group => !_index_by_code.has(_group.question_code));
    _known.sort((a, b) => _index_by_code.get(a.question_code) - _index_by_code.get(b.question_code));
    return _known.concat(_unknown);
}

// 백엔드 미답변 목록(items)을 문항(question_code) 단위로 묶는다.
// orderedQuestionCodes(선택) 가 있으면 그 순서(협력사 사이드바)로 정렬하고, 없으면 백엔드 순서를 유지한다.
function groupUnansweredByQuestion(items, orderedQuestionCodes) {
    const _groups = [];
    const _group_by_code = {};
    (Array.isArray(items) ? items : []).forEach((_item) => {
        if (!_item || !_item.question_code) return;
        let _group = _group_by_code[_item.question_code];
        if (!_group) {
            _group = {
                question_code: _item.question_code,
                question_name: _item.question_name || '',
                question_name_e: _item.question_name_e || '',
                question_name_c: _item.question_name_c || '',
                subs: []
            };
            _group_by_code[_item.question_code] = _group;
            _groups.push(_group);
        }
        _group.subs.push({
            sub_code: _item.sub_code, // 백엔드 키 — 그대로 둔다
            label: String(_item.label || _item.sub_code || '').trim(), // 실데이터에 선행 공백(' ST1') 이 있어 표시용만 정리
            sub_type: _item.sub_type || '',
            sub_type_label_key: subTypeLabelKey(_item.sub_type),
            reason_label_key: reasonLabelKey(_item.reason),
            missing_cells: Array.isArray(_item.missing_cells) ? _item.missing_cells : []
        });
    });
    return sortGroupsByOrder(_groups, orderedQuestionCodes);
}

// axios 에러가 "미답변 차단 409" 이면 items 배열을, 그 외(장애·다른 에러)면 null 을 돌려준다.
function extractUnansweredItems(error) {
    const _response = error && error.response;
    if (!_response || _response.status !== 409) return null;
    const _data = _response.data || {};
    if (_data.code !== UNANSWERED_REQUIRED_CODE) return null;
    return Array.isArray(_data.items) ? _data.items : [];
}

// template_contents 트리에서 question_code 가 일치하는 말단(category3) 노드를 찾는다.
// 찾은 노드는 문항 이동(QuestionList.pageGo)에 그대로 넘긴다.
function findQuestionNode(nodes, question_code) {
    for (const _node of (Array.isArray(nodes) ? nodes : [])) {
        if (!_node) continue;
        const _has_children = Array.isArray(_node.children) && _node.children.length > 0;
        if (!_has_children && _node.question_code === question_code) return _node;
        const _found = findQuestionNode(_node.children, question_code);
        if (_found) return _found;
    }
    return null;
}

module.exports = {
    BLOCK_UNANSWERED_OPTION,
    UNANSWERED_REQUIRED_CODE,
    SUB_TYPE_LABEL_KEYS,
    REASON_LABEL_KEYS,
    hasBlockUnansweredOption,
    subTypeLabelKey,
    reasonLabelKey,
    groupUnansweredByQuestion,
    extractUnansweredItems,
    findQuestionNode
};
